// OWNER: platform module. Offline indicator banner (NFR "Offline", ADR 0004). Mounted by AppShell under the
// read-only banner. Shows: offline state, changes waiting to be sent (with a Review drawer), "back online —
// sending", changes that couldn't be saved, and the "A new version is available — Reload" prompt.
// Only generic descriptions are shown ("New gift — saved on this device on 12 Oct 2026, 14:05"), never content.
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { CloudOff, RefreshCw } from 'lucide-react';
import { OFFLINE_ENTITY_LABELS } from '@seventide/types';
import { Banner, Button, ConfirmDialog, Drawer, StatusPill } from '../../../components/ui';
import { useMe } from '../../../hooks/useMe';
import { useOptionalWorkspace } from '../../../hooks/useWorkspace';
import { formatDateTime, plural } from '../../../lib/format';
import { offlineQueue, type Outbox } from './outbox';
import { applyServiceWorkerUpdate, useServiceWorkerUpdate } from './registerServiceWorker';
import type { OutboxItem } from './types';
import { useOfflineSync, useOutboxState } from './useOfflineSync';

export const OFFLINE_MESSAGE =
  "You're offline. New diary entries, gifts and payments will be saved on this device and sent when you're back online.";

const BACK_ONLINE_MS = 6_000;
const savedChanges = (n: number) => (n === 1 ? '1 saved change' : `${n} saved changes`);

function entityLabel(entityType: string): string {
  return OFFLINE_ENTITY_LABELS[entityType] ?? 'Record';
}

/** "New gift", "Change to a care diary entry", "Removal of a payment or transaction". */
export function describeOutboxItem(item: Pick<OutboxItem, 'entityType' | 'method'>): string {
  const label = entityLabel(item.entityType);
  const lower = label.charAt(0).toLowerCase() + label.slice(1);
  if (item.method === 'POST') return `New ${lower}`;
  if (item.method === 'DELETE') return `Removal of a ${lower}`;
  return `Change to a ${lower}`;
}

function statusText(item: OutboxItem): {
  tone: 'info' | 'warning' | 'danger';
  text: string;
  detail?: string;
} {
  switch (item.status) {
    case 'pending':
      return {
        tone: 'info',
        text: 'Waiting to be sent',
        detail: item.attempts > 0 ? 'Seventide will try again shortly.' : undefined,
      };
    case 'blocked':
      return {
        tone: 'warning',
        text: 'Waiting: the workspace is read-only',
        detail: 'It will be sent once the subscription is renewed.',
      };
    case 'conflict':
      return {
        tone: 'warning',
        text: 'Not saved: changed on another device',
        detail:
          item.lastError?.code === 'superseded'
            ? 'A newer change made on another device was kept. Your change was saved in the record history.'
            : 'Someone else changed this record first, so your change could not be applied.',
      };
    default:
      return {
        tone: 'danger',
        text: "Couldn't be saved",
        detail: item.lastError?.message ?? 'Seventide could not save this change.',
      };
  }
}

function useBackOnlineFlash(online: boolean): boolean {
  const [flash, setFlash] = useState(false);
  const prev = useRef(online);
  useEffect(() => {
    if (online && !prev.current) {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), BACK_ONLINE_MS);
      prev.current = online;
      return () => clearTimeout(t);
    }
    prev.current = online;
    return undefined;
  }, [online]);
  // Never show the flash while offline (derived, no extra state update).
  return online && flash;
}

export interface OfflineBannerProps {
  /** For tests. */
  outbox?: Outbox;
}

export function OfflineBanner({ outbox = offlineQueue }: OfflineBannerProps = {}) {
  const me = useMe();
  const ws = useOptionalWorkspace();
  const wsId = ws?.wsId ?? null;
  const userId = me.isPending ? undefined : (me.data?.user.id ?? null);
  useOfflineSync({ userId, wsId, outbox });
  const state = useOutboxState(outbox);
  const update = useServiceWorkerUpdate();
  const [reviewOpen, setReviewOpen] = useState(false);
  const backOnline = useBackOnlineFlash(state.online);

  const items = useMemo(
    () => state.items.filter((i) => !wsId || i.workspaceId === wsId),
    [state.items, wsId],
  );
  const waiting = items.filter((i) => i.status === 'pending' || i.status === 'blocked');
  const blocked = items.filter((i) => i.status === 'blocked');
  const problems = items.filter((i) => i.status === 'failed' || i.status === 'conflict');
  const waitingText = waiting.length
    ? `${plural(waiting.length, 'change')} ${waiting.length === 1 ? 'is' : 'are'} waiting to be sent.`
    : '';

  // One polite live region with the current headline (stays in the DOM so changes are announced).
  let announcement = '';
  if (!state.online) announcement = `${OFFLINE_MESSAGE} ${waitingText}`.trim();
  else if (state.syncing && waiting.length)
    announcement = `Back online. Sending ${savedChanges(waiting.length)}.`;
  else if (backOnline) announcement = waiting.length ? `Back online. ${waitingText}` : "You're back online.";
  else if (problems.length) announcement = "Some changes couldn't be saved.";
  if (update.waiting) announcement = `${announcement} A new version of Seventide is available.`.trim();

  const reviewButton =
    items.length > 0 ? (
      <Button variant="secondary" size="sm" onClick={() => setReviewOpen(true)}>
        Review {items.length === 1 ? 'change' : 'changes'}
      </Button>
    ) : null;
  const sendNow =
    state.online && waiting.length > 0 && !state.syncing ? (
      <Button
        variant="secondary"
        size="sm"
        leftIcon={<RefreshCw aria-hidden="true" className="h-5 w-5" />}
        onClick={() => void outbox.replay({ force: true })}
      >
        Send now
      </Button>
    ) : null;

  let main: ReactNode = null;
  if (!state.online) {
    main = (
      <Banner tone="warning" title="You're offline" action={reviewButton}>
        <span className="inline-flex items-start gap-2">
          <CloudOff aria-hidden="true" className="mt-1 hidden h-5 w-5 shrink-0 sm:inline" />
          <span>
            New diary entries, gifts and payments will be saved on this device and sent when you're back
            online.
            {waitingText && <strong className="block">{waitingText}</strong>}
          </span>
        </span>
      </Banner>
    );
  } else if (state.authPaused && waiting.length) {
    main = (
      <Banner tone="warning" title="Sign in to send your saved changes" action={reviewButton}>
        {waitingText} They are kept on this device and will be sent after you sign in again.
      </Banner>
    );
  } else if (state.syncing && waiting.length) {
    main = (
      <Banner tone="info" title="Back online — syncing">
        Sending {savedChanges(waiting.length)} to Seventide…
      </Banner>
    );
  } else if (blocked.length) {
    main = (
      <Banner tone="warning" title="Saved changes can't be sent yet" action={reviewButton}>
        {plural(blocked.length, 'change')} can't be sent while this workspace is read-only. They are kept on
        this device and will be sent once the subscription is renewed.
      </Banner>
    );
  } else if (waiting.length) {
    main = (
      <Banner
        tone="info"
        title="Changes waiting to be sent"
        action={
          <>
            {sendNow}
            {reviewButton}
          </>
        }
      >
        {waitingText} Seventide will keep trying automatically.
      </Banner>
    );
  } else if (backOnline) {
    main = (
      <Banner tone="success" title="Back online">
        {state.lastSyncedCount > 0 && state.lastSyncedAt
          ? `All saved changes have been sent (${state.lastSyncedCount}).`
          : 'Everything is up to date.'}
      </Banner>
    );
  }

  return (
    <>
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>
      {main}
      {problems.length > 0 && (
        <Banner tone="danger" title="Some changes couldn't be saved" action={reviewButton}>
          {plural(problems.length, 'change')} could not be saved. Review{' '}
          {problems.length === 1 ? 'it' : 'them'} to try again or remove{' '}
          {problems.length === 1 ? 'it' : 'them'} from this device.
        </Banner>
      )}
      {update.waiting && (
        <Banner
          tone="info"
          title="A new version of Seventide is available"
          action={
            <Button
              size="sm"
              onClick={applyServiceWorkerUpdate}
              loading={update.reloading}
              loadingText="Reloading"
            >
              Reload
            </Button>
          }
        >
          Reload to start using it. Anything waiting to be sent is kept.
        </Banner>
      )}
      <OutboxReviewDrawer
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        items={items}
        outbox={outbox}
        online={state.online}
      />
    </>
  );
}

function OutboxReviewDrawer({
  open,
  onOpenChange,
  items,
  outbox,
  online,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  items: OutboxItem[];
  outbox: Outbox;
  online: boolean;
}) {
  const waiting = items.filter((i) => i.status === 'pending' || i.status === 'blocked');
  const problems = items.filter((i) => i.status === 'failed' || i.status === 'conflict');
  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="Changes saved on this device"
      description="These changes were made while the connection was poor. Only a short description is shown here."
      footer={
        <>
          {online && waiting.length > 0 && (
            <Button variant="secondary" onClick={() => void outbox.replay({ force: true })}>
              Send now
            </Button>
          )}
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </>
      }
    >
      {items.length === 0 ? (
        <p>Everything has been sent. There is nothing waiting on this device.</p>
      ) : (
        <div className="flex flex-col gap-6">
          {waiting.length > 0 && (
            <section aria-labelledby="outbox-waiting-heading">
              <h3 id="outbox-waiting-heading" className="mb-2 text-lg font-bold">
                Waiting to be sent ({waiting.length})
              </h3>
              <OutboxList items={waiting} outbox={outbox} />
            </section>
          )}
          {problems.length > 0 && (
            <section aria-labelledby="outbox-problems-heading">
              <h3 id="outbox-problems-heading" className="mb-2 text-lg font-bold">
                Couldn't be saved ({problems.length})
              </h3>
              <OutboxList items={problems} outbox={outbox} />
            </section>
          )}
        </div>
      )}
    </Drawer>
  );
}

function OutboxList({ items, outbox }: { items: OutboxItem[]; outbox: Outbox }) {
  return (
    <ul className="flex flex-col gap-3">
      {items.map((item) => {
        const s = statusText(item);
        const title = describeOutboxItem(item);
        const canRetry = item.status === 'failed' || item.status === 'blocked';
        const isConflict = item.status === 'conflict';
        return (
          <li key={item.id} className="rounded-lg border border-border bg-surface p-3">
            <p className="font-bold">{title}</p>
            <p className="text-muted">Saved on this device on {formatDateTime(item.createdAt)}</p>
            <div className="mt-2">
              <StatusPill tone={s.tone}>{s.text}</StatusPill>
            </div>
            {s.detail && <p className="mt-1">{s.detail}</p>}
            <div className="mt-3 flex flex-wrap gap-2">
              {canRetry && (
                <Button size="sm" variant="secondary" onClick={() => void outbox.retry(item.id)}>
                  Try again
                </Button>
              )}
              {isConflict ? (
                <Button size="sm" variant="secondary" onClick={() => void outbox.discard(item.id)}>
                  Dismiss<span className="sr-only">: {title}</span>
                </Button>
              ) : (
                <ConfirmDialog
                  title="Remove this change?"
                  description="It hasn't been saved to Seventide yet. If you remove it, it will be lost."
                  confirmLabel="Remove change"
                  tone="danger"
                  onConfirm={() => outbox.discard(item.id)}
                  trigger={
                    <Button size="sm" variant="ghost">
                      Remove<span className="sr-only">: {title}</span>
                    </Button>
                  }
                />
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
